export default class OmeSender {
	constructor(url, stream, { onConnected, onClose } = {}) {
		this._url = url;
		this._stream = stream;
		this._onConnected = onConnected;
		this._onClose = onClose;
		this._ws = null;
		this._pc = null;
		this._senders = {};
		this._id = null;
		this._peerId = null;
		this._stopped = false;
	}

	start() {
		this._stopped = false;
		this._connect();
	}

	// Swap the published tracks without dropping the session. Restarting the sender instead would
	// close the OME publish, which the server reads as the caller leaving the call.
	// Returns false when the session can't absorb the new stream and has to be restarted.
	async replaceStream(stream) {
		this._stream = stream;
		if (!this._pc) return false;
		for (const kind of ['audio', 'video']) {
			const track = stream.getTracks().find(t => t.kind === kind) || null;
			const sender = this._senders[kind];
			if (!sender) {
				if (track) return false;
				continue;
			}
			if (track && kind === 'video') track.contentHint = 'motion';
			await sender.replaceTrack(track);
		}
		return true;
	}

	stop() {
		this._stopped = true;
		this._cleanup();
	}

	_connect() {
		let ws;
		try {
			ws = new WebSocket(this._url);
		} catch(e) {
			return;
		}
		this._ws = ws;

		ws.onopen = () => {
			ws.send(JSON.stringify({ command: 'request_offer' }));
		};

		ws.onmessage = (e) => {
			let msg;
			try { msg = JSON.parse(e.data); } catch(_) { return; }
			if (msg.command === 'offer') {
				this._handleOffer(msg);
			} else if (msg.command === 'candidate' && this._pc && msg.candidates) {
				msg.candidates.forEach(c => {
					this._pc.addIceCandidate(new RTCIceCandidate(c)).catch(() => {});
				});
			} else if (msg.command !== 'notification') {
				this._cleanup();
				if (!this._stopped && this._onClose) this._onClose();
			}
		};

		ws.onerror = () => {};

		ws.onclose = () => {
			this._cleanup();
			if (!this._stopped && this._onClose) this._onClose();
		};
	}

	async _handleOffer(msg) {
		this._id = msg.id;
		this._peerId = msg.peer_id;

		const pc = new RTCPeerConnection({ iceServers: [] });
		this._pc = pc;

		pc.onicecandidate = (e) => {
			if (e.candidate && e.candidate.candidate && this._ws && this._ws.readyState === WebSocket.OPEN) {
				this._ws.send(JSON.stringify({
					command: 'candidate',
					id: this._id,
					peer_id: this._peerId,
					candidates: [e.candidate],
				}));
			}
		};

		try {
			await pc.setRemoteDescription(new RTCSessionDescription(msg.sdp));

			this._senders = {};
			this._stream.getTracks().forEach(track => {
				if (track.kind === 'video') track.contentHint = 'motion';
				this._senders[track.kind] = pc.addTrack(track, this._stream);
			});

			if (msg.candidates) {
				msg.candidates.forEach(c => {
					pc.addIceCandidate(new RTCIceCandidate(c)).catch(() => {});
				});
			}

			const answer = await pc.createAnswer();
			await pc.setLocalDescription(answer);
			if (this._ws && this._ws.readyState === WebSocket.OPEN) {
				this._ws.send(JSON.stringify({
					command: 'answer',
					id: this._id,
					peer_id: this._peerId,
					sdp: answer,
				}));
			}

			if (!this._stopped && this._onConnected) this._onConnected();
		} catch(e) {
			this._cleanup();
			if (!this._stopped && this._onClose) this._onClose();
		}
	}

	_cleanup() {
		this._senders = {};
		if (this._pc) { this._pc.close(); this._pc = null; }
		if (this._ws) {
			this._ws.onclose = null;
			this._ws.onerror = null;
			this._ws.close();
			this._ws = null;
		}
	}
}
